import React, { useState, useContext, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, TestTube, Activity, Wrench, Settings } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FeatureFlagsContext } from '../Shared/FeatureFlags';
import toast from 'react-hot-toast';

import DevelopmentLayout from './shared/DevelopmentLayout';
import InfrastructureTools from './categories/InfrastructureTools';
import TestingTools from './categories/TestingTools';
import MonitoringTools from './categories/MonitoringTools';
import UtilityTools from './categories/UtilityTools';
import TestFeatureFlagComponent from '../Shared/TestFeatureFlagComponent';

const Development = () => {
  const { user } = useContext(AuthContext);
  const { featureFlags } = useContext(FeatureFlagsContext);
  const isFeatureEnabled = useFeatureFlag('newFeatureFlag');
  const [selectedCategory, setSelectedCategory] = useState('testing');
  const apiToastShown = useRef(false);

  // Test API connectivity once on component mount
  useEffect(() => {
    const testDebugEndpoint = async () => {
      try {
        const { default: api } = await import('../../services/api');
        const response = await api.get('/api/dev/debug/');
        console.log('Debug endpoint response:', response);
        
        if (!apiToastShown.current) {
          toast.success(
            <div>
              <p className="font-semibold">API Connection Success</p>
              <p className="text-sm mt-1">Developer Tools API is available</p>
            </div>
          );
          apiToastShown.current = true;
        }
      } catch (error) {
        console.error('Debug endpoint error:', error);
        toast.error(
          <div>
            <p className="font-semibold">API Connection Failed</p>
            <p className="text-sm mt-1">{error.message || 'Could not connect to Developer Tools API'}</p>
          </div>
        );
      }
    };

    const timer = setTimeout(() => {
      testDebugEndpoint();
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const toolCategories = [
    {
      id: 'testing',
      label: 'Testing & QA',
      icon: TestTube,
      description: 'Test application features and functionality',
      component: TestingTools
    },
    {
      id: 'infrastructure', 
      label: 'Infrastructure',
      icon: Settings,
      description: 'Server management and system administration',
      component: InfrastructureTools
    },
    {
      id: 'monitoring',
      label: 'Monitoring',
      icon: Activity,
      description: 'System health and performance monitoring',
      component: MonitoringTools
    },
    {
      id: 'utilities',
      label: 'Utilities',
      icon: Wrench,
      description: 'Development utilities and helper tools',
      component: UtilityTools
    }
  ];

  const renderContent = () => {
    const selectedCat = toolCategories.find(cat => cat.id === selectedCategory) || toolCategories[0];
    const Component = selectedCat.component;
    
    return (
      <motion.div
        key={selectedCategory}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="space-y-6"
      >
        <div className="flex flex-col gap-1">
          <h2 className="flex items-center gap-2.5 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <selectedCat.icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            {selectedCat.label}
          </h2>
          <p className="pl-[30px] text-sm text-gray-500 dark:text-gray-400">{selectedCat.description}</p>
        </div>
        <Component user={user} />
      </motion.div>
    );
  };

  return (
    <DevelopmentLayout 
      title="Developer Tools"
      categories={toolCategories}
      selectedCategory={selectedCategory}
      onCategoryChange={setSelectedCategory}
    >
      <AnimatePresence mode="wait">
        {renderContent()}
      </AnimatePresence>

      {/* Feature Flag Test Component */}
      {isFeatureEnabled && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="card mt-10 p-6"
        >
          <h2 className="mb-5 flex items-center gap-2.5 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Feature Flag Test Component
          </h2>
          <TestFeatureFlagComponent />
        </motion.div>
      )}
    </DevelopmentLayout>
  );
};

export default Development;
